// @ts-ignore
import { useMelwaysInfo } from '@seek/melways-react';
import { adapter, getScriptTag } from '@seek/tealium-adapter';

import { useTypedSelector } from 'src/reducers';

const jobsrecsMapping = ['wru', 'newjob'];

const events = {
  // views
  UNSUBSCRIBED_SUCCESS_PAGE_VIEWED:
    'notification_unsubscribe_success_displayed',
  UNSUBSCRIBED_FAILED_PAGE_VIEWED: 'notification_unsubscribe_failed_displayed',
  UNSUBSCRIBED_ALL_FAILED_PAGE_VIEWED: 'notification_unsubscribe_all_failed',
  SUBSCRIBED_FAILED_PAGE_VIEWED: 'notification_subscribe_failed_displayed',
  SUBSCRIBED_SUCCESS_PAGE_VIEWED: 'notification_subscribe_success_displayed',
  UNSUBSCRIBED_ALL_SUCCESS_VIEWED: 'notification_unsubscribe_all_succeeded',

  // links
  NOTI_PREF_TOGGLED: 'toggle_pressed',
  NOTI_PREF_OPTION_SELECTED: 'option_selection_pressed',
  CUSTOMISE_BUTTON_CLICK: 'settings_section_displayed',
  NOTI_UNSUBSCRIBE_SUCCESS: 'notification unsubscribe success',
  NOTI_UNSUBSCRIBE_FAILED: 'notification unsubscribe failed',
  NOTI_SUBSCRIBE_SUCCESS: 'notification subscribe success',
  NOTI_SUBSCRIBE_FAILED: 'notification subscribe failed',
  TOGGLED_ON: 'on',
  TOGGLED_OFF: 'off',
} as const;

const PREF_PAGE_TITLE = 'notification settings';

const tealiumAdapter = adapter();

const convertToApacProductName = (product: string | undefined) => {
  if (product === undefined) {
    return undefined;
  }

  let apacProductName = product;

  if (jobsrecsMapping.includes(product.toLowerCase())) {
    apacProductName = 'jobrecs';
  }

  return apacProductName;
};

export const getTealiumScriptTag: (env: string | null) => string = (env) =>
  env
    ? getScriptTag({
        account: 'seek',
        profile: 'candidate-main',
        environment: env,
      })
    : '';

let candidateId: number;
let profileGuid: string;

let trackingContext: TealiumTrackingContext;

interface TealiumTrackingContext {
  brand: string;
  siteCountry: string;
  zone: string;
  siteLanguage: string;
}

const commonPageFields = () => ({
  siteSection: 'myaccount',
  loginId: candidateId,
  profileGuid,
  isLoggedIn: Boolean(candidateId),
  userAgent: global?.navigator?.userAgent,
  ...trackingContext,
});

export const setCandidateId = (
  id: number,
  emailCorrelationId: string,
): void => {
  candidateId = id;
  profileGuid = emailCorrelationId;
};

export const setTrackingContext = (
  brand: string,
  country: string,
  zone: string,
  language: string,
): void => {
  trackingContext = {
    brand,
    siteCountry: country,
    zone,
    siteLanguage: language,
  };
};

export const useSetTrackingContext = () => {
  const brand = useTypedSelector((state) => state.page.pageAnalytics.brand);
  const country = useTypedSelector(
    (state) => state.page.pageAnalytics.siteCountry,
  );
  const zone = useTypedSelector((state) => state.page.pageAnalytics.zone);
  const { language } = useMelwaysInfo();

  setTrackingContext(brand, country, zone, language);
};

export const trackToggle = ({
  product,
  channel,
  subscribed,
  frequency,
  category,
}: {
  product: string;
  channel: string;
  subscribed: boolean;
  frequency?: string;
  category?: string;
}): void => {
  const trackingProduct = convertToApacProductName(product);

  tealiumAdapter.link(events.NOTI_PREF_TOGGLED, {
    ...commonPageFields(),
    currentPage: PREF_PAGE_TITLE,
    ...(category && { currentPageSection: category }),
    eventName: events.NOTI_PREF_TOGGLED,
    toggleType: `${trackingProduct} ${channel}`,
    toggleState: subscribed ? 'on' : 'off',
    ...(frequency ? { notificationFrequency: frequency } : {}),
    isExitLink: false,
  });
};

export const trackOptionSelectionPressed = ({
  product,
  channel,
  optionSelectionType,
  optionSelected,
}: {
  product: string;
  channel: string;
  optionSelectionType: string;
  optionSelected: string;
}): void => {
  const trackingProduct = convertToApacProductName(product);

  tealiumAdapter.link(events.NOTI_PREF_OPTION_SELECTED, {
    ...commonPageFields(),
    currentPage: PREF_PAGE_TITLE,
    eventName: events.NOTI_PREF_OPTION_SELECTED,
    optionSelectionType,
    optionSelected,
    optionSelectionName: `${trackingProduct} ${channel} frequency`,
    currentPageSection: `${trackingProduct} ${channel}`,
    isExitLink: false,
  });
};

export const trackUnsubscribeSuccessPageLoad = (
  product: string | undefined,
  channel: string | undefined,
) => {
  const trackingProduct = convertToApacProductName(product);

  tealiumAdapter.view(events.UNSUBSCRIBED_SUCCESS_PAGE_VIEWED, {
    ...commonPageFields(),
    currentPage: events.NOTI_UNSUBSCRIBE_SUCCESS,
    notificationType: trackingProduct,
    notificationChannel: channel,
  });
};

export const trackUnsubscribeAllSuccessPageLoad = (
  product: string | undefined,
  channel: string | undefined,
) => {
  const trackingProduct = convertToApacProductName(product);

  tealiumAdapter.view(events.UNSUBSCRIBED_ALL_SUCCESS_VIEWED, {
    ...commonPageFields(),
    currentPage: events.NOTI_UNSUBSCRIBE_SUCCESS,
    notificationType: trackingProduct,
    notificationChannel: channel,
  });
};

export const trackUnsubscribeFailedPageLoad = () => {
  tealiumAdapter.view(events.UNSUBSCRIBED_FAILED_PAGE_VIEWED, {
    ...commonPageFields(),
    currentPage: events.NOTI_UNSUBSCRIBE_FAILED,
  });
};

export const trackUnsubscribeAllFailedPageLoad = () => {
  tealiumAdapter.view(events.UNSUBSCRIBED_ALL_FAILED_PAGE_VIEWED, {
    ...commonPageFields(),
    currentPage: events.NOTI_UNSUBSCRIBE_FAILED,
  });
};

export const trackSubscribeSuccessPageLoad = (
  product: string | undefined,
  channel: string | undefined,
) => {
  const trackingProduct = convertToApacProductName(product);

  tealiumAdapter.view(events.SUBSCRIBED_SUCCESS_PAGE_VIEWED, {
    ...commonPageFields(),
    currentPage: events.NOTI_SUBSCRIBE_SUCCESS,
    notificationType: trackingProduct,
    notificationChannel: channel,
  });
};

export const trackSubscribeFailedPageLoad = () => {
  tealiumAdapter.view(events.SUBSCRIBED_FAILED_PAGE_VIEWED, {
    ...commonPageFields(),
    currentPage: events.NOTI_SUBSCRIBE_FAILED,
  });
};

export const trackCustomiseButtonClick = (category: string) => {
  tealiumAdapter.link(events.CUSTOMISE_BUTTON_CLICK, {
    ...commonPageFields(),
    currentPage: PREF_PAGE_TITLE,
    eventName: events.CUSTOMISE_BUTTON_CLICK,
    currentPageSection: category,
  });
};

export const trackTogglePreferencesCategory = (
  categoryCode: string,
  subscribed: boolean,
) => {
  tealiumAdapter.link(events.NOTI_PREF_TOGGLED, {
    ...commonPageFields(),
    eventName: events.NOTI_PREF_TOGGLED,
    currentPage: PREF_PAGE_TITLE,
    toggleType: categoryCode,
    toggleState: subscribed ? events.TOGGLED_ON : events.TOGGLED_OFF,
  });
};
